import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const HeroSection = () => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [[current, direction], setCurrent] = useState([0, 0]);

  useEffect(() => {
    // Using Statamic's built-in API
    fetch('/api/pages/home')
      .then(response => {
        console.log('API Response Status:', response.status);
        if (!response.ok) throw new Error('API response was not ok');
        return response.json();
      })
      .then(data => {
        console.log('Full API Response:', data);
        setPageContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('API Error:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Extract hero images from Statamic content
  const heroImages = pageContent?.homepage_sections?.[0]?.hero_slides || [];
  
  // Transform Statamic data - handle both string paths and asset objects
  const transformedImages = heroImages.map(slide => {
    let imageUrl = '';
    
    // Handle different possible formats of image data
    if (typeof slide.image === 'string') {
      // If it's a string path
      imageUrl = `/storage/${slide.image}`;
    } else if (slide.image && slide.image.url) {
      // If it's an asset object with url property
      imageUrl = slide.image.url;
    } else if (slide.image && slide.image.path) {
      // If it's an asset object with path property
      imageUrl = `/storage/${slide.image.path}`;
    }
    
    console.log('Processed image:', slide.image, '->', imageUrl);
    
    return {
      src: imageUrl,
      alt: slide.alt_text || 'Eye&U Kenya hero image'
    };
  });

  // Fallback to default images if no Statamic images
  const imagesToUse = transformedImages.filter(img => img.src).length > 0 
    ? transformedImages 
    : [
        {
          src: "https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=800",
          alt: "Eye specialist examining patient at Eye&U Kenya clinic"
        },
        {
          src: "https://images.pexels.com/photos/5752242/pexels-photo-5752242.jpeg?auto=compress&cs=tinysrgb&w=800",
          alt: "Modern LASIK equipment in use at Eye&U Kenya"
        },
        {
          src: "https://images.pexels.com/photos/6749778/pexels-photo-6749778.jpeg?auto=compress&cs=tinysrgb&w=800",
          alt: "Family receiving eye care at Eye&U Kenya"
        }
      ];

  const next = () =>
    setCurrent(([prev]) => [(prev + 1) % imagesToUse.length, 1]);

  const prev = () =>
    setCurrent(([prev]) => [(prev - 1 + imagesToUse.length) % imagesToUse.length, -1]);

  // Auto-play every 5s
  useEffect(() => {
    const interval = setInterval(() => {
      next();
    }, 5000);
    return () => clearInterval(interval);
  }, [imagesToUse.length]);

  if (loading) {
    return (
      <section className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-red-50 overflow-hidden">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-text-secondary">Loading hero images...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-red-50 overflow-hidden">
        <div className="text-center p-6 bg-red-50 rounded-lg">
          <p className="text-red-600">Error loading hero images: {error}</p>
          <p className="text-sm text-red-500 mt-2">Using default images as fallback</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-gray-50 to-red-50 overflow-hidden"
      aria-label="Eye&U Kenya Hero Gallery"
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23eb2227' fill-opacity='0.1'%3E%3Cpath d='M30 30l15-15v30l-15-15zm-15 0L0 15v30l15-15z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Image Carousel */}
      <div className="relative w-full h-[60vh] sm:h-[70vh] md:h-[90vh] max-w-7xl mx-auto flex items-center justify-center">
        <figure className="relative w-full h-full overflow-hidden rounded-2xl shadow-xl">
          <AnimatePresence initial={false} custom={direction}>
            <motion.img
              key={current}
              src={imagesToUse[current].src}
              alt={imagesToUse[current].alt}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.8}
              onDragEnd={(e, { offset, velocity }) => {
                if (offset.x > 100 || velocity.x > 500) {
                  prev();
                } else if (offset.x < -100 || velocity.x < -500) {
                  next();
                }
              }}
              className="absolute w-full h-full object-cover"
            />
          </AnimatePresence>
        </figure>

        {/* Navigation (always visible) */}
        <div className="absolute inset-0 flex items-center justify-between px-2">
          <button
            onClick={prev}
            aria-label="Previous image"
            className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition"
          >
            <span aria-hidden="true" className="text-2xl text-primary">&#8592;</span>
          </button>
          <button
            onClick={next}
            aria-label="Next image"
            className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition"
          >
            <span aria-hidden="true" className="text-2xl text-primary">&#8594;</span>
          </button>
        </div>

        {/* Indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {imagesToUse.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent([idx, idx > current ? 1 : -1])}
              aria-label={`Go to image ${idx + 1}`}
              className={`w-3 h-3 rounded-full transition ${
                idx === current ? 'bg-primary' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 right-10 w-20 h-20 bg-red-100 rounded-full opacity-20 animate-pulse" />
      <div className="absolute bottom-20 left-10 w-16 h-16 bg-primary/10 rounded-full opacity-30 animate-pulse" style={{ animationDelay: '1s' }} />
    </section>
  );
};

// Animation Variants (moved outside component)
const variants = {
  enter: (direction) => ({
    x: direction > 0 ? 200 : -200,
    opacity: 0,
    scale: 0.9
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      x: { type: "spring", stiffness: 120, damping: 20 },
      opacity: { duration: 0.6 },
      scale: { duration: 0.6 }
    }
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 200 : -200,
    opacity: 0,
    scale: 1.05,
    transition: {
      x: { type: "spring", stiffness: 120, damping: 20 },
      opacity: { duration: 0.4 },
      scale: { duration: 0.4 }
    }
  })
};

export default HeroSection;